import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useToast } from '../contexts/ToastContext';
import { Sparkles, Loader2, CheckCircle2 } from 'lucide-react';

interface AnalyzeButtonProps {
  onAnalyzed: () => void;
}

export default function AnalyzeButton({ onAnalyzed }: AnalyzeButtonProps) {
  const { user } = useAuth();
  const { showError, showInfo } = useToast();
  const [analyzing, setAnalyzing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleAnalyze = async () => {
    if (!user) return;

    setAnalyzing(true);
    showInfo('Analyzing your feedback with AI...');

    try {
      const { data: allResponses, error: responsesError } = await supabase
        .from('feedback_responses')
        .select(`
          *,
          feedback_requests!inner(
            requester_id
          )
        `)
        .eq('feedback_requests.requester_id', user.id);

      if (responsesError) throw responsesError;

      if (!allResponses || allResponses.length === 0) {
        showError('No feedback responses found to analyze');
        return;
      }

      const validResponses = allResponses.filter(r => {
        const hasTextContent = r.raw_feedback && r.raw_feedback.trim().length > 0;
        const hasCompletedAudio = r.processing_status === 'completed' && r.transcription && r.transcription.trim().length > 0;
        return hasTextContent || hasCompletedAudio;
      });

      const pendingTranscriptions = allResponses.filter(r =>
        r.audio_url && (r.processing_status === 'pending' || r.processing_status === 'processing') && !r.raw_feedback
      ).length;

      if (validResponses.length < 3) {
        if (pendingTranscriptions > 0) {
          showError(`You have ${validResponses.length} completed responses and ${pendingTranscriptions} still being transcribed. Please wait for transcriptions to finish.`);
        } else {
          showError(`You need at least 3 completed feedback responses to generate an AI summary. You currently have ${validResponses.length}.`);
        }
        return;
      }

      const feedbackTexts = validResponses
        .map(r => r.transcription || r.raw_feedback)
        .filter(Boolean) as string[];

      if (feedbackTexts.length === 0) {
        showError('No feedback text available to analyze');
        return;
      }

      // Call the analyze-feedback edge function
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session) {
        showError('Authentication required');
        return;
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-feedback`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to analyze feedback: ${response.statusText}`);
      }

      // Edge function handles the database upsert, so we just need to wait for success
      await response.json();

      setShowSuccessModal(true);
    } catch (error: any) {
      console.error('Analysis error:', error);
      showError(error.message || 'Failed to analyze feedback');
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <>
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[24px] max-w-md w-full shadow-2xl p-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-[#1F2937] mb-2">Thank You!</h2>
            <p className="text-[#6B7280] mb-6">
              Your feedback has been analyzed successfully using AI. View your personalized insights and development areas below.
            </p>
            <button
              onClick={() => {
                setShowSuccessModal(false);
                onAnalyzed();
              }}
              className="bg-[#8CAFA8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#7A9B94] transition"
            >
              Done
            </button>
          </div>
        </div>
      )}
      <button
      onClick={handleAnalyze}
      disabled={analyzing}
      className="bg-gradient-to-r from-[#8CAFA8] to-[#7A9B94] text-white px-8 py-4 rounded-lg font-semibold hover:from-[#7A9B94] hover:to-[#6B8A83] disabled:opacity-50 disabled:cursor-not-allowed transition shadow-[0_2px_12px_rgba(0,0,0,0.04)] flex items-center gap-3 mx-auto"
    >
      {analyzing ? (
        <>
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Analyzing with AI...</span>
        </>
      ) : (
        <>
          <Sparkles className="w-6 h-6" />
          <span>Analyze Feedback with AI</span>
        </>
      )}
    </button>
    </>
  );
}
